import React, {useCallback, useEffect, useState, useRef} from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Dimensions,
  FlatList,
} from 'react-native';
import {
  Text,
  Appbar,
  ActivityIndicator,
  Button,
  Chip,
  Snackbar,
  RadioButton,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useSubscriptionStore} from '../../store/useSubscriptionStore';
import {colors, spacing} from '../../theme';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

const {width: SCREEN_WIDTH} = Dimensions.get('window');
const CARD_WIDTH = SCREEN_WIDTH * 0.78;
const CARD_MARGIN = spacing.sm;

const FEATURE_ICONS = {
  STAFF_LIMIT: 'account-group',
  CUSTOMER_LIMIT: 'account-multiple',
  ADVANCED_REPORTS: 'chart-bar',
  WHATSAPP_AUTOMATION: 'whatsapp',
  SMS_AUTOMATION: 'message-text',
};

const PlanSelectionScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {orgUnitId, orgUnitName, subscriptionId} = route.params || {};

  const {plans, isLoading, fetchPlans} = useSubscriptionStore();
  const [selectedFamilyIndex, setSelectedFamilyIndex] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [snackbar, setSnackbar] = useState('');
  const flatListRef = useRef(null);

  // Filter out trial plans - only show paid plans
  const paidPlans = plans.filter(p => p.planType === 'PAID');

  useEffect(() => {
    fetchPlans().catch(e =>
      setSnackbar(e.response?.data?.message || t('common.error')),
    );
  }, [fetchPlans, t]);

  // Auto-select first variant when plans load or family changes
  useEffect(() => {
    if (paidPlans.length > 0 && paidPlans[selectedFamilyIndex]) {
      const variants = paidPlans[selectedFamilyIndex].variants || [];
      if (variants.length > 0 && !selectedVariantId) {
        setSelectedVariantId(variants[0].id);
      }
    }
  }, [paidPlans, selectedFamilyIndex, selectedVariantId]);

  const handleFamilyScroll = useCallback(
    event => {
      const offsetX = event.nativeEvent.contentOffset.x;
      const index = Math.round(offsetX / (CARD_WIDTH + CARD_MARGIN * 2));
      if (index !== selectedFamilyIndex && index >= 0 && index < paidPlans.length) {
        setSelectedFamilyIndex(index);
        const variants = paidPlans[index]?.variants || [];
        if (variants.length > 0) {
          setSelectedVariantId(variants[0].id);
        }
      }
    },
    [selectedFamilyIndex, paidPlans],
  );

  const handleProceed = useCallback(() => {
    if (!selectedVariantId) {
      setSnackbar(t('subscription.selectVariant'));
      return;
    }

    const family = paidPlans[selectedFamilyIndex];
    const variant = family?.variants?.find(v => v.id === selectedVariantId);
    if (!variant) return;

    navigation.navigate('SubscriptionInvoice', {
      orgUnitId,
      orgUnitName,
      subscriptionId,
      planFamily: family,
      planVariant: variant,
    });
  }, [
    selectedVariantId,
    selectedFamilyIndex,
    paidPlans,
    orgUnitId,
    orgUnitName,
    subscriptionId,
    navigation,
    t,
  ]);

  const renderFeature = useCallback((feature) => {
    const icon = FEATURE_ICONS[feature.featureCode] || 'check-circle';
    const enabled = feature.isEnabled;

    let label = feature.featureCode.replace(/_/g, ' ');
    // Capitalize first word only
    label = label.charAt(0) + label.slice(1).toLowerCase();

    if (feature.limitValue) {
      label += `: ${feature.limitValue}`;
      if (feature.limitUnit) {
        label += ` ${feature.limitUnit}`;
      }
    } else if (enabled && !feature.limitValue) {
      // Boolean feature like ADVANCED_REPORTS
    }

    return (
      <View key={feature.id} style={styles.featureRow}>
        <MaterialCommunityIcons
          name={enabled ? icon : 'close-circle-outline'}
          size={18}
          color={enabled ? colors.success : colors.textLight}
        />
        <Text
          style={[
            styles.featureText,
            !enabled && styles.featureDisabled,
          ]}>
          {label}
        </Text>
      </View>
    );
  }, []);

  const renderPlanCard = useCallback(
    ({item: family, index}) => {
      const isSelected = index === selectedFamilyIndex;
      const variants = family.variants || [];
      const features = family.features || [];

      return (
        <View
          style={[
            styles.planCard,
            isSelected && styles.planCardSelected,
          ]}>
          {/* Plan header */}
          <View style={styles.planHeader}>
            <Text style={styles.planName}>{family.name}</Text>
            {variants.length > 0 && (
              <Text style={styles.planStartingPrice}>
                {t('subscription.startingAt')} {'\u20B9'}
                {variants[0].netPrice}
                /{variants[0].durationCount === 1
                  ? t('subscription.month')
                  : `${variants[0].durationCount} ${t('subscription.months')}`}
              </Text>
            )}
          </View>

          {/* Features */}
          <View style={styles.featuresContainer}>
            <Text style={styles.featuresTitle}>{t('subscription.features')}</Text>
            {features.map(renderFeature)}
          </View>

          {/* Variant selection */}
          <View style={styles.variantsContainer}>
            <Text style={styles.variantsTitle}>{t('subscription.chooseDuration')}</Text>
            {variants.map(variant => {
              const hasDiscount =
                variant.discountPct &&
                parseFloat(variant.discountPct) > 0;

              return (
                <View
                  key={variant.id}
                  style={[
                    styles.variantRow,
                    selectedVariantId === variant.id &&
                      styles.variantRowSelected,
                  ]}>
                  <RadioButton.Android
                    value={String(variant.id)}
                    status={
                      selectedVariantId === variant.id
                        ? 'checked'
                        : 'unchecked'
                    }
                    onPress={() => setSelectedVariantId(variant.id)}
                    color={colors.primary}
                  />
                  <View style={styles.variantInfo}>
                    <Text style={styles.variantName}>{variant.name}</Text>
                    <View style={styles.variantPriceRow}>
                      {hasDiscount && (
                        <Text style={styles.listPrice}>
                          {'\u20B9'}{variant.listPrice}
                        </Text>
                      )}
                      <Text style={styles.netPrice}>
                        {'\u20B9'}{variant.netPrice}
                      </Text>
                      {hasDiscount && (
                        <Chip
                          style={styles.discountChip}
                          textStyle={styles.discountChipText}
                          compact>
                          {variant.discountPct}% off
                        </Chip>
                      )}
                    </View>
                  </View>
                </View>
              );
            })}
          </View>
        </View>
      );
    },
    [selectedFamilyIndex, selectedVariantId, renderFeature, t],
  );

  if (isLoading) {
    return (
      <View style={styles.container}>
        <Appbar.Header style={styles.appbar} dark>
          <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
          <Appbar.Content title={t('subscription.choosePlan')} titleStyle={styles.appbarTitle} />
        </Appbar.Header>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title={t('subscription.choosePlan')} titleStyle={styles.appbarTitle} />
      </Appbar.Header>

      {/* Store name banner */}
      {orgUnitName && (
        <View style={styles.storeBanner}>
          <MaterialCommunityIcons name="store" size={18} color={colors.primary} />
          <Text style={styles.storeBannerText}>{orgUnitName}</Text>
        </View>
      )}

      {/* Plan dots indicator */}
      <View style={styles.dotsRow}>
        {paidPlans.map((_, i) => (
          <View
            key={i}
            style={[
              styles.dot,
              i === selectedFamilyIndex && styles.dotActive,
            ]}
          />
        ))}
      </View>

      {/* Horizontally scrollable plan cards */}
      <FlatList
        ref={flatListRef}
        data={paidPlans}
        renderItem={renderPlanCard}
        keyExtractor={item => String(item.id)}
        horizontal
        pagingEnabled={false}
        snapToInterval={CARD_WIDTH + CARD_MARGIN * 2}
        snapToAlignment="start"
        decelerationRate="fast"
        showsHorizontalScrollIndicator={false}
        onMomentumScrollEnd={handleFamilyScroll}
        contentContainerStyle={styles.carouselContent}
        style={styles.carousel}
      />

      {/* Subscribe button */}
      <View style={styles.bottomBar}>
        <Button
          mode="contained"
          onPress={handleProceed}
          buttonColor={colors.primary}
          contentStyle={styles.subscribeButtonContent}
          style={styles.subscribeButton}
          disabled={!selectedVariantId}>
          {t('subscription.proceedToInvoice')}
        </Button>
      </View>

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},

  storeBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary + '10',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    gap: spacing.sm,
  },
  storeBannerText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.primary,
  },

  dotsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    gap: 6,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.border,
  },
  dotActive: {
    width: 20,
    backgroundColor: colors.primary,
  },

  carousel: {
    flex: 1,
  },
  carouselContent: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
  },

  // Plan card
  planCard: {
    width: CARD_WIDTH,
    marginHorizontal: CARD_MARGIN,
    backgroundColor: colors.surface,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: colors.border,
    overflow: 'hidden',
  },
  planCardSelected: {
    borderColor: colors.primary,
    elevation: 4,
    shadowColor: colors.primary,
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.15,
    shadowRadius: 8,
  },
  planHeader: {
    backgroundColor: colors.primary,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
  },
  planName: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.white,
  },
  planStartingPrice: {
    fontSize: 14,
    color: colors.white + 'CC',
    marginTop: 4,
  },

  // Features
  featuresContainer: {
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  featuresTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 4,
  },
  featureText: {
    fontSize: 14,
    color: colors.text,
    flex: 1,
  },
  featureDisabled: {
    color: colors.textLight,
    textDecorationLine: 'line-through',
  },

  // Variants
  variantsContainer: {
    padding: spacing.md,
  },
  variantsTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },
  variantRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingRight: spacing.sm,
    borderRadius: 8,
    marginBottom: 4,
  },
  variantRowSelected: {
    backgroundColor: colors.primary + '0D',
  },
  variantInfo: {
    flex: 1,
  },
  variantName: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text,
  },
  variantPriceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 2,
  },
  listPrice: {
    fontSize: 13,
    color: colors.textLight,
    textDecorationLine: 'line-through',
  },
  netPrice: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.primary,
  },
  discountChip: {
    backgroundColor: colors.success + '18',
    height: 22,
  },
  discountChipText: {
    fontSize: 11,
    color: colors.success,
  },

  // Bottom bar
  bottomBar: {
    padding: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
  },
  subscribeButton: {
    borderRadius: 8,
  },
  subscribeButtonContent: {
    paddingVertical: spacing.xs,
  },
});

export default PlanSelectionScreen;
